import React from 'react';
import { Building2, Mail, Receipt, CheckCircle2, ArrowRight, Star } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BRAND, { BASE_URL } from '@/brand';

const products = [
  {
    icon: <Building2 size={40} className="text-brand-500" />,
    name: 'HentCVR',
    tagline: 'Virksomhedsdata på sekunder',
    description: 'Søg på CVR-numre og firmanavne — få øjeblikkelig adgang til stamdata, finansielle nøgletal, ejerforhold og beslutningstagere.',
    features: [
      'CVR & stamdata fra offentlige registre',
      'Finansielle nøgletal og årsregnskaber',
      'Ejerforhold og selskabsdiagram',
      'Beslutningstager kontakter',
    ],
    price: '249 kr/md',
    href: 'https://hentcvr.dk',
    color: 'blue',
    current: false,
  },
  {
    icon: <Mail size={40} className="text-emerald-500" />,
    name: BRAND.name,
    tagline: 'Digital post direkte i indbakken',
    description: 'Modtag al din virksomheds digitale post fra Virk og E-Boks direkte på email — uden manuelle logins.',
    features: [
      'Samler post fra Virk og E-Boks',
      'Automatisk videresendelse',
      'Multi-virksomhed og multi-bruger',
      'GDPR-kompatibel, EU-hostet',
    ],
    price: '49 kr/md',
    href: BASE_URL,
    color: 'emerald',
    current: true,
  },
  {
    icon: <Receipt size={40} className="text-highlight-500" />,
    name: 'HentBilag',
    tagline: 'Automatisk bilagsmatching',
    description: 'Gennemsøger Gmail og Outlook for kvitteringer og fakturaer — matcher dem mod manglende posteringer i e-conomic og Pleo.',
    features: [
      'Gmail + Outlook integration',
      'e-conomic og Pleo support',
      'OCR på billedfiler og PDF',
      'Bulk godkendelse og download',
    ],
    price: '149 kr/md',
    href: 'https://hentbilag.dk',
    color: 'indigo',
    current: false,
  },
];

const colorMap = {
  blue: {
    badge: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
    ring: 'ring-brand-500/40',
    icon_bg: 'bg-brand-500/10',
    button: 'bg-btn hover:bg-btn-hover text-btn-fg',
  },
  emerald: {
    badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    ring: 'ring-emerald-500/40',
    icon_bg: 'bg-emerald-500/10',
    button: 'bg-emerald-600 hover:bg-emerald-500 text-white',
  },
  indigo: {
    badge: 'bg-highlight-500/10 text-highlight-400 border-highlight-500/20',
    ring: 'ring-highlight-500/40',
    icon_bg: 'bg-highlight-500/10',
    button: 'bg-highlight-600 hover:bg-highlight-500 text-white',
  },
};

function BundlePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-th-body">
      {/* Nav */}
      <nav className="always-dark border-b border-[#1e293b] bg-[#111827] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-th-text-secondary hover:text-th-text transition-colors text-sm">
            <ArrowRight size={16} className="rotate-180" />
            Tilbage til {BRAND.name}
          </button>
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1">
              <Building2 size={18} className="text-brand-500" />
              <Mail size={18} className="text-emerald-500" />
              <Receipt size={18} className="text-highlight-500" />
            </div>
            <span className="font-bold text-th-text text-sm">Bundle-tilbud</span>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <Badge className="mb-6 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
            <Star size={12} className="mr-1" /> Spar over 40%
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-th-text mb-4">
            Alle tre produkter<br />
            <span className="text-emerald-500">199 kr/md</span>
          </h1>
          <p className="text-lg text-th-text-secondary mb-3 max-w-xl mx-auto">
            Du har allerede {BRAND.name}. Tilføj HentCVR og HentBilag og få det fulde billede —
            ét abonnement, én faktura, ingen binding.
          </p>
          <p className="text-sm text-th-text-muted mb-10">
            Separat pris: 447 kr/md · Bundle: 199 kr/md · Du sparer 248 kr/md
          </p>
          <a href={`mailto:${BRAND.supportEmail}?subject=Bundle-abonnement – ${BRAND.name}-kunde`}>
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-500 text-white text-lg px-10">
              Kontakt os om bundle
            </Button>
          </a>
          <p className="text-xs text-th-text-muted mt-3">Ekskl. moms · Ingen binding · Svar inden for 1 arbejdsdag</p>
        </motion.div>
      </section>

      {/* Produktkort */}
      <section className="pb-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-6">
            {products.map((p, idx) => {
              const c = colorMap[p.color];
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className={`p-7 bg-th-input border-th-border h-full flex flex-col relative ${p.current ? `ring-2 ${c.ring}` : ''}`}>
                    {p.current && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-3 py-1 text-xs font-medium bg-brand-600 text-white rounded-full">Dit nuværende produkt</span>
                      </div>
                    )}

                    <div className={`w-14 h-14 rounded-xl ${c.icon_bg} flex items-center justify-center mb-5`}>
                      {p.icon}
                    </div>

                    <Badge className={`mb-3 w-fit text-xs ${c.badge}`}>{p.price}</Badge>

                    <h3 className="text-xl font-heading font-bold text-th-text mb-1">{p.name}</h3>
                    <p className="text-th-text-secondary text-sm mb-3 font-medium">{p.tagline}</p>
                    <p className="text-th-text-secondary text-sm leading-relaxed mb-5 flex-grow">{p.description}</p>

                    <ul className="space-y-2 mb-6">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-th-text-secondary text-sm">
                          <CheckCircle2 size={15} className="text-emerald-500 shrink-0 mt-0.5" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    {p.current ? (
                      <Button variant="outline" className="w-full border-th-border-light text-th-text-secondary" disabled>
                        Allerede aktivt
                      </Button>
                    ) : (
                      <a href={p.href} target="_blank" rel="noreferrer">
                        <Button className={`w-full ${c.button} flex items-center justify-center gap-2`}>
                          Gå til {p.name} <ArrowRight size={15} />
                        </Button>
                      </a>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Bundle opsummering */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-10 p-8 bg-th-input border border-th-border rounded-2xl text-center"
          >
            <h2 className="text-2xl font-heading font-bold text-th-text mb-2">
              Tag alle tre for <span className="text-brand-500">199 kr/md</span>
            </h2>
            <p className="text-th-text-secondary mb-6 max-w-lg mx-auto text-sm">
              Vi sætter det op for dig — kontakt os og vi aktiverer bundle på din konto samme dag.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mb-6">
              {[
                { label: 'HentCVR Basic', price: '249 kr', color: 'text-brand-400' },
                { label: BRAND.bundle.ownProduct, price: BRAND.bundle.ownPrice, color: 'text-emerald-400' },
                { label: 'HentBilag', price: '149 kr', color: 'text-highlight-400' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 px-4 py-2 bg-th-body border border-th-border rounded-lg text-sm">
                  <CheckCircle2 size={14} className="text-emerald-500" />
                  <span className="text-th-text-secondary">{item.label}</span>
                  <span className={`${item.color} font-medium`}>{item.price}</span>
                </div>
              ))}
            </div>
            <a href={`mailto:${BRAND.supportEmail}?subject=Bundle-abonnement – ${BRAND.name}-kunde`}>
              <Button size="lg" className="bg-btn hover:bg-btn-hover text-btn-fg px-10">
                Kontakt os om bundle
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default BundlePage;
